<script setup lang="ts">
    import { computed, ref, shallowRef, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { hostToolFromPage, type RegisteredHostTool } from 'composables'
    import { usePageTools } from '~/composables/usePageTools'
    import { usePagePresence } from '~/composables/usePagePresence'
    import { usePagePointer } from '~/composables/usePagePointer'
    import { useDialog, useSafeLocalStorage } from 'composables'
    import {
        useWidgetEvents,
        usePageControlSignal,
        useWorkingSignal,
    } from '~/utils/emitWidgetEvent'
    import { useChatbotAuth } from '~/composables/useChatbotAuth'
    import { useChatbotStoreSync } from '~/composables/useChatbotStoreSync'
    import { useChatbotProfileProvider } from '~/composables/useChatbotProfileProvider'
    import { useChatbotAgent } from '~/composables/useChatbotAgent'
    import { useChatbotAuthForm } from '~/composables/useChatbotAuthForm'
    import { useFocusTrapBypass } from '~/composables/useFocusTrapBypass'
    import { usePendingMessage } from '~/composables/usePendingMessage'
    import {
        DEFAULT_BACKEND_URL,
        resolveWidgetAppearance,
        resolveWidgetLayout,
    } from '~/utils'
    import { useCopilotPageCss } from '~/composables/useCopilotPageCss'
    import { AgentWidgetAppearance, AgentWidgetLayout } from 'models'
    import {
        initializeTelemetry,
        setTelemetryContext,
        trackAppEvent,
        trackAppException,
    } from '~/modules/applicationInsights'
    import PkChatbot from 'components/src/chat/PkChatbot.vue'
    import PkPageControlBar from 'components/src/chat/PkPageControlBar.vue'
    import PkPagePointer from 'components/src/chat/PkPagePointer.vue'
    import PkHostCallConfirmDialog from 'components/src/chat/PkHostCallConfirmDialog.vue'
    import { usePageModal, usePageModalStop } from '~/composables/usePageModal'
    import PkChatbotAuth from 'components/src/chat/PkChatbotAuth.vue'
    import PkLoader from 'components/src/PkLoader.vue'
    import PkWelcomePopup from 'components/src/chat/PkWelcomePopup.vue'
    import PkWorkingBeam from 'components/src/chat/PkWorkingBeam.vue'
    import PkWorkingAurora from 'components/src/chat/PkWorkingAurora.vue'
    import PkAgentPresence from 'components/src/chat/PkAgentPresence.vue'
    import { isAchromaticColor } from 'components/src/chat/utils'

    defineOptions({
        inheritAttrs: false,
    })

    useFocusTrapBypass()

    const props = withDefaults(
        defineProps<{
            agentId: string
            organizationSlug: string
            /** The backend that hosts the agent: the Eight Wave cloud unless a dedicated installation sets its own. */
            baseUrl?: string
            externalToken?: string
            /** Boolean or attribute string ("true"/"false") when set via HTML attribute. */
            awaitExternalToken?: boolean | string
            /**
             * Host-session mode: the widget skips its own sign-in and uses
             * the host platform's better-auth session from the cookie.
             * Boolean or attribute string ("true"/"false").
             */
            useHostSession?: boolean | string
            floatingButtonPosition?: 'left' | 'right'
            /** `floating` or `copilot`: wins over the agent's setting (spec widget-copilot.md). */
            layout?: string
            /** Floating layout only, `solid` or `glass`: wins over the agent's setting. */
            appearance?: string
            customCss?: string
            /** Override the session/anonymous cookie name (defaults to 'eight-wave-ai'). */
            cookieName?: string
            /**
             * Force the chat color scheme instead of following the OS
             * `prefers-color-scheme`. Let a host app hand down its own theme.
             */
            systemTheme?: 'light' | 'dark'
        }>(),
        {
            baseUrl: DEFAULT_BACKEND_URL,
            externalToken: undefined,
            awaitExternalToken: false,
            useHostSession: false,
            floatingButtonPosition: undefined,
            layout: undefined,
            appearance: undefined,
            customCss: undefined,
            cookieName: undefined,
            systemTheme: undefined,
        },
    )

    // An empty attribute counts as none: requests must never go to the page's
    // own origin.
    const baseUrl = props.baseUrl || DEFAULT_BACKEND_URL

    setTelemetryContext({
        organizationSlug: props.organizationSlug,
        agentId: props.agentId,
    })

    // dialog
    const { PkGlobalDialog } = useDialog()

    // Widget events (must be called during setup to capture the component instance)
    const { emit: emitWidgetEvent } = useWidgetEvents()

    // chatbot ref
    const chatbotEl = ref<InstanceType<typeof PkChatbot>>()

    // external context set by host application
    const externalContext = ref<Record<string, unknown> | undefined>()

    // tools the host page registered and runs itself (spec host-tools.md)
    const hostToolRegistry = shallowRef<RegisteredHostTool[]>([])

    // open and close chat widget
    const isChatOpen = useSafeLocalStorage(
        computed(() => `${props.agentId}-is-open`),
        false,
    )
    const toggleChatOpen = () => {
        isChatOpen.value = !isChatOpen.value
        trackAppEvent(isChatOpen.value ? 'widget_opened' : 'widget_closed')
        emitWidgetEvent(isChatOpen.value ? 'opened' : 'closed')

        // stop generation when closing the chat while it's generating
        if (!isChatOpen.value) {
            chatbotEl.value?.stopGeneration()
        }
    }

    const {
        session,
        onError: onAuthError,
        headers,
        token,
        userId,
        isExternalAuth,
        signInWithEmail,
        twoFactorChallenge,
        verifyTwoFactor,
        sendTwoFactorCode,
        cancelTwoFactor,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        signInWithExternalToken,
        signOut,
    } = useChatbotAuth({
        baseUrl,
        externalToken: () => props.externalToken,
        awaitExternalToken:
            !!props.awaitExternalToken && props.awaitExternalToken !== 'false',
        useHostSession: () =>
            !!props.useHostSession && props.useHostSession !== 'false',
        isActive: isChatOpen,
        cookieName: props.cookieName,
        onSignedIn: (detail) => emitWidgetEvent('signed-in', detail),
        onSignedOut: () => emitWidgetEvent('signed-out'),
        onExternalAuthExpired: () => emitWidgetEvent('external-auth-expired'),
        onExternalTokenExpiring: () =>
            emitWidgetEvent('external-token-expiring'),
    })

    // Enrich telemetry with userId once auth completes
    watch(userId, (id) => {
        if (id) {
            setTelemetryContext({ userId: id })
        }
    })

    // The chat store outlives PkChatbot (v-if'd on isChatOpen): keep the
    // session identity mirrored into it so a login performed while the chat
    // is closed resets the store right away.
    const { t } = useI18n({ useScope: 'global' })

    const chatStore = useChatbotStoreSync({
        agentId: props.agentId,
        baseUrl,
        headers,
        userId,
    })

    // the `working` attribute and events, for the host page (spec widget-copilot.md)
    useWorkingSignal(() => chatStore.isWorking)
    usePageControlSignal(() => chatStore.pageControlActive)

    // A modal dialog of the page makes the chat inert: the card the agent
    // waits on moves to the top layer, and the stop goes inside that dialog
    // (spec page-tools.md)
    const pageModal = usePageModal(
        () => !!chatStore.hostConfirmation || chatStore.pageControlActive,
    )
    usePageModalStop(pageModal, () => chatStore.pageControlActive, {
        text: () => t('messagePart.pageControlActive'),
        stopLabel: () => t('action.stop'),
        onStop: () => void chatStore.stopHostActions(),
    })

    // a message the host page sent before the chat could take it; closing
    // the panel drops it, the user dismissed the chat
    const pendingMessage = usePendingMessage(
        chatbotEl,
        () => chatStore.localChatId,
    )
    watch(isChatOpen, (open) => {
        if (!open) {
            pendingMessage.clear()
        }
    })

    // emit 'ready' once per signed-in user: a refetch of the same session
    // is not news, another user is
    watch(userId, (id) => {
        if (id) {
            emitWidgetEvent('ready')
        }
    })

    function onError(error: { type: string; title: string; status: number }) {
        trackAppException(new Error(`${error.type}: ${error.title}`), {
            status: String(error.status),
        })
        emitWidgetEvent('chat-error', {
            type: error.type,
            title: error.title,
            status: error.status,
        })
        onAuthError(error)
    }

    const {
        agent,
        branding,
        authMethods,
        deploymentCss,
        deployment,
        requiresAuth,
        notFound,
        isLoading,
        contrastColor,
        themeClass,
        themeStyle,
        reload,
    } = useChatbotAgent({
        baseUrl,
        organizationSlug: () => props.organizationSlug,
        agentId: () => props.agentId,
        headers,
        systemTheme: () => props.systemTheme,
    })

    // browser telemetry, as the installation asks: none until it says so
    watch(deployment, (current) => {
        if (current?.telemetry) {
            initializeTelemetry({ backendUrl: baseUrl, ...current.telemetry })
        }
    })

    // page tools of the widget (spec page-tools.md), and the cursor that
    // shows where they act (spec page-tools-improvements.md, M12)
    const presence = usePagePresence(() => chatStore.pageControlActive)
    const {
        registry: pageToolRegistry,
        forms: pageForms,
        registerPages,
        pointing,
    } = usePageTools(() => agent.value?.capabilities?.pageAccess, t, presence)
    const presenceTarget = presence.target

    // What the user points at on the page for the next message (spec
    // page-point-and-choose.md, P): the input's button starts it, and only
    // a widget on a page offers it
    const pagePointer = usePagePointer({
        pointing,
        // Not while an answer runs, as the input's own button
        available: () => chatStore.pagePointerAvailable && !chatStore.isWorking,
        chatOpen: () => isChatOpen.value,
        onPointed: (pointer) => {
            chatStore.pagePointer = pointer
        },
    })
    chatStore.pagePointerStart = pagePointer.start
    watch(pagePointer.picking, (picking) => {
        chatStore.pagePointing = picking
    })
    const presenceClicks = presence.clicks
    const presenceHome = () => chatbotEl.value?.$el as Element | undefined

    // Read-only profile context + voluntary sign-in affordance shared by both
    // widgets. Provides ChatbotProfileContextKey for the chat components.
    const { authBranding } = useChatbotProfileProvider({
        agent,
        branding,
        authMethods,
        session,
        signOut,
        isExternalAuth,
        frontendUrl: () => deployment.value?.frontendUrl,
        token,
        baseUrl,
        headers,
        cookieName: props.cookieName,
    })

    const effectivePosition = computed(
        () =>
            props.floatingButtonPosition ??
            agent.value?.interface.floatingButtonPosition ??
            branding.value?.interface.floatingButtonPosition ??
            'right',
    )

    // The copilot layout (spec widget-copilot.md): the panel docked on one
    // side at full height, the site framed beside it, and the beam along the
    // frame while the agent works.
    const isCopilot = computed(
        () =>
            resolveWidgetLayout(
                props.layout,
                agent.value?.interface.layout ??
                    branding.value?.interface.layout,
            ) === AgentWidgetLayout.Copilot,
    )
    // The light while the agent works: the beam along the copilot's panel
    const showsBeam = computed(
        () =>
            agent.value?.interface.workingGlow !== false && chatStore.isWorking,
    )
    // On the site only while the agent acts on it, the beam on the
    // copilot's frame or the aurora of the floating layout: an answer that
    // only thinks and writes lights the panel, not the page it leaves alone
    const showsPageBeam = computed(
        () => showsBeam.value && chatStore.pageControlActive,
    )
    // A gray agent color (the platform's own agent is black) draws a beam
    // that reads as a dark border, not a light, and a dark aurora: the
    // accent lights them instead, as in Agent Studio, and the cursor takes
    // it too, to match
    const beamStyle = computed(() =>
        isAchromaticColor(agent.value?.interface.mainColor)
            ? {
                  '--pk-working-beam-color': 'var(--color-accent)',
                  '--pk-working-aurora-color': 'var(--color-accent)',
                  '--pk-agent-presence-color': 'var(--color-accent)',
              }
            : undefined,
    )
    // The floating card as frosted glass over the site
    const isGlass = computed(
        () =>
            !isCopilot.value &&
            resolveWidgetAppearance(
                props.appearance,
                agent.value?.interface.appearance ??
                    branding.value?.interface.appearance,
            ) === AgentWidgetAppearance.Glass,
    )
    // The page opens the chat with a button of its own (`open()`)
    const hidesButton = computed(
        () =>
            (agent.value?.interface.hideFloatingButton ??
                branding.value?.interface.hideFloatingButton) === true,
    )
    // The agent's CSS for the page's own elements, while docked (spec D7)
    useCopilotPageCss({
        css: () =>
            agent.value?.interface.copilotPageCss ??
            branding.value?.interface.copilotPageCss,
        active: () => isCopilot.value && isChatOpen.value,
    })
    // The copilot slides its panel in from its side, in step with the frame
    // and the page, while it opens and closes; a panel giving way to another
    // (the loader to the chat, once signed in) fades, as the floating card
    // always does. The span is `--chatbot-copilot-duration` in the styles.
    const COPILOT_MOTION_MS = 450
    const panelMoving = ref(false)
    let panelMovingTimer: ReturnType<typeof setTimeout> | undefined
    watch(isChatOpen, () => {
        panelMoving.value = true
        clearTimeout(panelMovingTimer)
        panelMovingTimer = setTimeout(() => {
            panelMoving.value = false
        }, COPILOT_MOTION_MS)
    })
    const panelTransition = computed(() =>
        isCopilot.value && panelMoving.value
            ? 'floating-chat-widget-copilot'
            : undefined,
    )

    const mergedCss = computed(() => {
        const parts = [
            agent.value?.interface?.customCss,
            branding.value?.interface.customCss,
            props.customCss,
        ].filter(Boolean)
        return parts.length ? parts.join('\n') : undefined
    })

    // Login screen (private agents)
    const {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
        handleVerifyTwoFactor,
        handleSendTwoFactorCode,
        handleCancelTwoFactor,
    } = useChatbotAuthForm({
        signInWithEmail,
        isTwoFactorPending: () => !!twoFactorChallenge.value,
        verifyTwoFactor,
        sendTwoFactorCode,
        cancelTwoFactor,
        signUpWithEmail,
        requestPasswordReset,
        frontendUrl: () => deployment.value?.frontendUrl,
        signInWithSocialPopup,
        refetchSession: () => session.value.refetch(),
        reload,
    })

    // Public API exposed on the custom element DOM node
    defineExpose({
        open() {
            if (!isChatOpen.value) {
                toggleChatOpen()
            }
        },
        close() {
            if (isChatOpen.value) {
                toggleChatOpen()
            }
        },
        toggle() {
            toggleChatOpen()
        },
        /** Opens the panel when it is closed: the user sees the message and its answer. */
        sendMessage(text: string) {
            if (!text.trim()) {
                return
            }
            if (!isChatOpen.value) {
                toggleChatOpen()
            }
            pendingMessage.send(text)
        },
        startNewChat() {
            pendingMessage.invalidate()
            void chatStore.startNewChat()
        },
        setContext(context: Record<string, unknown>) {
            externalContext.value = context
        },
        /** Same shape as WebMCP's `navigator.modelContext.registerTool`; a name registered again replaces the old tool. */
        registerTool(tool: RegisteredHostTool) {
            hostToolRegistry.value = [
                ...hostToolRegistry.value.filter(
                    ({ name }) => name !== tool.name,
                ),
                hostToolFromPage(tool),
            ]
        },
        unregisterTool(name: string) {
            hostToolRegistry.value = hostToolRegistry.value.filter(
                (tool) => tool.name !== name,
            )
        },
        /** The pages of the site the agent may open without asking (spec `page-tools-improvements.md`, M6); each call replaces the list. */
        registerPages,
        signIn: signInWithExternalToken,
        signOut,
        get isOpen() {
            return isChatOpen.value
        },
        get isWorking() {
            return chatStore.isWorking
        },
        get isControllingPage() {
            return chatStore.pageControlActive
        },
    })
</script>

<template>
    <div
        class="floating-chat-widget"
        :class="[
            themeClass,
            {
                'floating-chat-widget--position-left':
                    effectivePosition === 'left',
                'floating-chat-widget--copilot': isCopilot,
                'floating-chat-widget--glass': isGlass,
                'floating-chat-widget--no-button': hidesButton,
                'floating-chat-widget--acting': chatStore.pageControlActive,
                'floating-chat-widget--pointing': pagePointer.picking.value,
            },
        ]"
        :style="themeStyle">
        <!-- #region styles -->
        <!-- The deployment theme in a sheet of its own, before the agent's
             CSS: what follows it wins, and an @import at the top of the
             agent's CSS stays at the top of its sheet. -->
        <component :is="'style'" v-if="deploymentCss">{{
            deploymentCss
        }}</component>
        <component :is="'style'" v-if="mergedCss">{{ mergedCss }}</component>
        <!-- #endregion -->

        <!-- #region copilot frame: the site in a window beside the docked
             panel (from `xl` up), or a backdrop under the drawer (`md` to
             `xl`). Nothing is written on the page: both live in the shadow
             root and let every click through but the backdrop's. -->
        <Transition name="floating-chat-widget-frame">
            <div
                v-if="isCopilot && isChatOpen"
                class="floating-chat-widget__frame"
                aria-hidden="true">
                <div
                    class="floating-chat-widget__frame-window"
                    :style="beamStyle">
                    <PkWorkingBeam :active="showsPageBeam" />
                </div>
            </div>
        </Transition>
        <Transition name="floating-chat-widget-frame">
            <div
                v-if="isCopilot && isChatOpen"
                class="floating-chat-widget__backdrop"
                @click="toggleChatOpen" />
        </Transition>
        <!-- The floating layout frames nothing: an aurora grows from the
             card's corner instead, under the card, letting every click
             through -->
        <div
            v-if="!isCopilot"
            class="floating-chat-widget__page-aurora"
            :style="beamStyle"
            aria-hidden="true">
            <PkWorkingAurora
                :active="showsPageBeam"
                :side="effectivePosition === 'left' ? 'left' : 'right'"
                :pulse="presenceClicks" />
        </div>
        <!-- #endregion -->

        <!-- #region launcher and welcome popup: in the copilot layout the
             open panel closes from its own header -->
        <Transition name="slide-bottom">
            <div
                v-if="
                    (agent || branding) &&
                    !notFound &&
                    !hidesButton &&
                    !(isCopilot && isChatOpen)
                "
                class="floating-chat-widget__launcher">
                <PkWelcomePopup
                    :agent-id
                    :message="agent?.interface.initialMessage"
                    :enabled="!isChatOpen && agent?.interface?.welcomePopup"
                    @open="toggleChatOpen" />
                <button
                    type="button"
                    :aria-label="
                        isChatOpen
                            ? $t('action.closeChat')
                            : $t('action.openChat')
                    "
                    :aria-expanded="isChatOpen"
                    class="floating-chat-widget__button"
                    @click.stop="toggleChatOpen">
                    <svg
                        v-if="isChatOpen"
                        class="w-full h-full"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="currentColor">
                        <path
                            d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
                    </svg>
                    <svg
                        v-else
                        class="w-full h-full"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="currentColor">
                        <path
                            d="M12 1.99996C12.8632 1.99996 13.701 2.10973 14.5 2.31539L14 4.25192C13.3608 4.0874 12.6906 3.99997 12 3.99997C7.58174 3.99997 4.00002 7.58172 4 12C4 13.3344 4.3255 14.6174 4.93945 15.7656L5.28906 16.4189L4.63379 19.3662L7.58105 18.7109L8.23438 19.0605C9.38255 19.6745 10.6656 20 12 20C16.4183 20 20 16.4183 20 12C20 11.6771 19.9805 11.3587 19.9434 11.0459L21.9297 10.8095C21.976 11.1999 22 11.5972 22 12C22 17.5228 17.5228 22 12 22C10.2975 22 8.69425 21.5746 7.29102 20.8242L2 22L3.17578 16.709C2.42541 15.3057 2 13.7025 2 12C2.00002 6.47714 6.47717 1.99996 12 1.99996ZM19.5293 1.3193C19.7058 0.893513 20.2942 0.8935 20.4707 1.3193L20.7236 1.93063C21.1555 2.97343 21.9615 3.80614 22.9746 4.2568L23.6914 4.57614C24.1022 4.75882 24.1022 5.35635 23.6914 5.53903L22.9326 5.87692C21.945 6.3162 21.1534 7.11943 20.7139 8.1279L20.4668 8.69333C20.2863 9.10747 19.7136 9.10747 19.5332 8.69333L19.2861 8.1279C18.8466 7.11942 18.0551 6.3162 17.0674 5.87692L16.3076 5.53903C15.8974 5.35618 15.8974 4.75895 16.3076 4.57614L17.0254 4.2568C18.0384 3.80614 18.8445 2.97343 19.2764 1.93063L19.5293 1.3193Z"></path>
                    </svg>
                </button>
            </div>
        </Transition>
        <!-- #endregion -->

        <!-- #region chat -->
        <Transition :name="panelTransition">
            <div
                v-if="
                    isChatOpen &&
                    (requiresAuth || isLoading) &&
                    !(agent && session.data)
                "
                class="floating-chat-widget__chat floating-chat-widget__auth">
                <PkChatbotAuth
                    v-if="requiresAuth"
                    v-model:error-message="authError"
                    v-model:success="authSuccess"
                    :branding
                    :auth-methods
                    :base-url="baseUrl"
                    :loading="authLoading"
                    :two-factor-methods="twoFactorChallenge?.methods"
                    @sign-in="handleSignIn"
                    @sign-up="handleSignUp"
                    @request-reset="handleReset"
                    @social="handleSocial"
                    @verify-two-factor="handleVerifyTwoFactor"
                    @send-two-factor-code="handleSendTwoFactorCode"
                    @cancel-two-factor="handleCancelTwoFactor" />
                <PkLoader v-else />
                <!-- These panels have no header of their own to close from,
                     and where they take the whole screen the button is under
                     them (the copilot hides it while open) -->
                <span class="floating-chat-widget__close">
                    <VvButton
                        modifiers="action-quiet"
                        icon="ri:close-line"
                        :title="$t('action.close')"
                        @click.stop="toggleChatOpen" />
                </span>
            </div>
        </Transition>
        <Transition :name="panelTransition">
            <div
                v-if="isChatOpen && !requiresAuth && !agent && !isLoading"
                class="floating-chat-widget__chat floating-chat-widget__loading">
                <PkLoader />
                <span class="floating-chat-widget__close">
                    <VvButton
                        modifiers="action-quiet"
                        icon="ri:close-line"
                        :title="$t('action.close')"
                        @click.stop="toggleChatOpen" />
                </span>
            </div>
        </Transition>
        <Transition :name="panelTransition">
            <PkChatbot
                v-if="agent && isChatOpen && session.data"
                :key="agentId"
                ref="chatbotEl"
                class="floating-chat-widget__chat"
                :modifier="isCopilot ? 'panel' : undefined"
                :base-url
                :agent-id
                :user-id
                :name="agent.name"
                :agent-interface="agent.interface"
                :agent-file-upload="agent.capabilities?.fileUpload"
                :voice="agent.capabilities?.voice"
                :headers
                :system-theme="systemTheme"
                :actions="['upvote', 'downvote', 'feedback', 'regenerate']"
                :external-context="externalContext"
                :host-tools="agent.capabilities?.hostTools"
                :host-tool-registry="hostToolRegistry"
                :page-access="agent.capabilities?.pageAccess"
                :page-tool-registry="pageToolRegistry"
                :page-forms="pageForms"
                @error="onError"
                @loaded="pendingMessage.onLoaded">
                <template #actions="{ useColorsForAgentHeader }">
                    <VvButton
                        :modifiers="
                            useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet'
                        "
                        icon="ri:close-line"
                        :title="$t('action.close')"
                        @click.stop="toggleChatOpen" />
                </template>
                <!-- Voluntary sign-in view for anonymous users (chat dropdown) -->
                <template #view-auth>
                    <PkChatbotAuth
                        v-model:error-message="authError"
                        v-model:success="authSuccess"
                        :branding="authBranding"
                        :auth-methods
                        :loading="authLoading"
                        :two-factor-methods="twoFactorChallenge?.methods"
                        @sign-in="handleSignIn"
                        @sign-up="handleSignUp"
                        @request-reset="handleReset"
                        @social="handleSocial"
                        @verify-two-factor="handleVerifyTwoFactor"
                        @send-two-factor-code="handleSendTwoFactorCode"
                        @cancel-two-factor="handleCancelTwoFactor" />
                </template>
            </PkChatbot>
        </Transition>
        <!-- The beam on the panel's own border, where there is no frame
             (below `xl`): a box of its own over the panel, the chat clips
             what it holds -->
        <div
            v-if="isCopilot && isChatOpen"
            class="floating-chat-widget__panel-beam"
            :style="beamStyle"
            aria-hidden="true">
            <PkWorkingBeam :active="showsBeam" />
        </div>
        <!-- #endregion -->

        <!-- #region page control bar: the agent acting on the page, and the
             user's stop (spec page-tools.md). After the chat: on the same
             top layer, the later one paints above, so the bar and its Stop
             stay above a chat that takes the whole screen. -->
        <PkAgentPresence
            :active="chatStore.pageControlActive"
            :target="presenceTarget"
            :agent-name="agent?.name"
            :home="presenceHome"
            :above="pageModal"
            :style="beamStyle"
            @arrived="presence.arrived" />
        <PkPageControlBar
            :active="chatStore.pageControlActive"
            :action="chatStore.pageAction?.title"
            :chat="chatbotEl?.$el"
            @stop="chatStore.stopHostActions()" />
        <PkPagePointer
            :picking="pagePointer.picking.value"
            :box="pagePointer.box.value"
            :bubble="pagePointer.bubble.value"
            :style="beamStyle"
            @cancel="pagePointer.stop()"
            @ask="pagePointer.ask()"
            @move="pagePointer.move"
            @press="pagePointer.press" />
        <PkHostCallConfirmDialog
            v-if="chatStore.hostConfirmation && pageModal"
            :question="
                chatStore.hostConfirmation.question ?? t('toolApproval.prompt')
            "
            :details="chatStore.hostConfirmation.details"
            :unchanged="chatStore.hostConfirmation.unchanged"
            @respond="chatStore.respondHostConfirmation" />
        <!-- #endregion -->

        <!-- #region global dialogs -->
        <PkGlobalDialog />
        <!-- #endregion -->
    </div>
</template>

<style lang="scss">
    @use '~/assets/scss/style';

    .floating-chat-widget {
        display: contents;

        // The measures of the copilot layout, on the block and not on its
        // modifier: a panel and a frame still leaving after a switch to the
        // floating layout keep them, and finish their animation
        --chatbot-copilot-gap: var(--spacing-12);
        --chatbot-copilot-panel-width: clamp(320px, 24vw, 406px);

        // Agent Studio's copilot
        --chatbot-copilot-duration: 0.45s;
        --chatbot-copilot-easing: cubic-bezier(0.32, 0.72, 0, 1);

        // On a page that lets the screen run under the notch and the home
        // indicator (`viewport-fit=cover`) the panel keeps clear of them;
        // `env()` is 0 everywhere else
        --chatbot-copilot-top: calc(
            env(safe-area-inset-top) + var(--chatbot-copilot-gap)
        );
        --chatbot-copilot-right: calc(
            env(safe-area-inset-right) + var(--chatbot-copilot-gap)
        );
        --chatbot-copilot-bottom: calc(
            env(safe-area-inset-bottom) + var(--chatbot-copilot-gap)
        );
        --chatbot-copilot-left: calc(
            env(safe-area-inset-left) + var(--chatbot-copilot-gap)
        );

        @media (prefers-reduced-motion: reduce) {
            --chatbot-copilot-duration: 0.01ms;
        }

        &__launcher {
            position: fixed;
            bottom: var(--spacing-16);
            right: var(--spacing-16);
            width: fit-content;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: var(--spacing-14);
            background: transparent;
            z-index: 99999997; // under iubenda banner but above everything else
        }

        &__button {
            align-items: center;
            justify-content: center;
            padding: var(--spacing-10);
            border: 0px;
            width: var(--spacing-48);
            height: var(--spacing-48);
            border-radius: var(--rounded-full);
            background-color: var(
                --chatbot-main-color,
                var(--color-black, #000)
            );
            color: var(--chatbot-contrast-color, var(--color-white, #fff));
            box-shadow: rgb(0 0 0 / 20%) 0px 4px 8px 0px;
            cursor: pointer;
            pointer-events: auto;
            transition: 0.2s ease-in-out;
            display: block;
            transform: scale(1);
            flex-shrink: 0;

            &:hover {
                transform: scale(1.1);
            }
        }

        &__chat {
            position: fixed;
            inset: 0;
            height: auto;
            overflow: hidden;
            z-index: 2147483647; // max z-index to ensure it's always on top

            @include media-breakpoint-up('sm', $breakpoints) {
                inset: auto 16px 78px auto;
                border-radius: var(--rounded-xxl);
                width: 406px;
                height: 85dvh;
                box-shadow:
                    0px 260px 73px 0px rgb(0 0 0 / 0%),
                    0px 166px 67px 0px rgb(0 0 0 / 1%),
                    0px 94px 56px 0px rgb(0 0 0 / 2%),
                    0px 42px 42px 0px rgb(0 0 0 / 3%),
                    0px 10px 23px 0px rgb(0 0 0 / 4%);
                backdrop-filter: blur(4px);
            }

            // Full screen, below the breakpoint above
            @media (width < 576px) {
                transition: top var(--chatbot-copilot-duration)
                    var(--chatbot-copilot-easing);
            }
        }

        // While the agent acts on the site, a phone's full screen chat makes
        // room for it: a sheet at the bottom, the site above it with the
        // agent's cursor and ring, which the chat would hide (the layer cuts
        // the chat out). Full screen again when the agent is done
        &--acting .floating-chat-widget__chat {
            @media (width < 576px) {
                top: 62dvh;
                border-radius: var(--rounded-xxl) var(--rounded-xxl) 0 0;
                box-shadow: var(--shadow-xl);
            }
        }

        // While the user points at the site (spec page-point-and-choose.md,
        // P) what covers it steps aside: the floating card, and the
        // copilot's drawer with its backdrop. From `xl` up the copilot's
        // panel sits beside the site, and stays
        &--pointing .floating-chat-widget__chat,
        &--pointing .floating-chat-widget__backdrop,
        &--pointing .floating-chat-widget__panel-beam {
            visibility: hidden;
        }

        &--pointing.floating-chat-widget--copilot .floating-chat-widget__chat,
        &--pointing.floating-chat-widget--copilot
            .floating-chat-widget__panel-beam {
            @include media-breakpoint-up('xl', $breakpoints) {
                visibility: visible;
            }
        }

        // There the bar that says how to point sits over the site's window,
        // not over the whole screen
        &--copilot .pk-page-pointer__bar {
            @include media-breakpoint-up('xl', $breakpoints) {
                left: calc(
                    (
                            100% - var(--chatbot-copilot-panel-width) - var(
                                    --chatbot-copilot-gap
                                )
                        ) /
                        2
                );
            }
        }

        &--copilot.floating-chat-widget--position-left .pk-page-pointer__bar {
            @include media-breakpoint-up('xl', $breakpoints) {
                left: calc(
                    (
                            100% + var(--chatbot-copilot-panel-width) +
                                var(--chatbot-copilot-gap)
                        ) /
                        2
                );
            }
        }

        // The window, for the aurora while the agent acts on the site, under
        // the card and the open button: the floating layout has no frame for
        // it
        &__page-aurora {
            position: fixed;
            inset: 0;
            z-index: 99999996;
            pointer-events: none;
        }

        &__loading {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--color-surface, #fff);
        }

        &__auth {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--color-surface, #fff);
        }

        // The close of the sign-in and loading panels, which have no header:
        // only where they take the whole screen and cover the button
        &__close {
            position: absolute;
            top: calc(env(safe-area-inset-top) + var(--spacing-8));
            right: calc(env(safe-area-inset-right) + var(--spacing-8));

            @include media-breakpoint-up('sm', $breakpoints) {
                display: none;
            }
        }

        &--position-left {
            .floating-chat-widget__launcher {
                right: unset;
                left: var(--spacing-16);
                align-items: flex-start;
            }

            .floating-chat-widget__chat {
                @include media-breakpoint-up('sm', $breakpoints) {
                    right: unset;
                    left: 16px;
                }
            }
        }

        // #region glass appearance of the floating card: the site shows
        // through it, blurred, from `sm` where the chat is a card; full
        // screen on a phone it stays solid. Inside, the chat drops its own
        // surface, and the agent's bubbles, the notices and the field are
        // lighter panes of the same glass: the card already blurs the site,
        // so they only tint it. The user's bubbles are that glass tinted
        // with the agent's color, dense enough for its contrast text. The
        // rim catches the light at the top left and fades toward the bottom
        // right, each pane has a lit top edge, and a deeper shadow than the
        // solid card's floats it over the site.
        @supports (backdrop-filter: blur(1px)) {
            &--glass .floating-chat-widget__chat {
                @include media-breakpoint-up('sm', $breakpoints) {
                    --chatbot-glass-pane: color-mix(
                        in srgb,
                        var(--color-surface) 70%,
                        transparent
                    );
                    --chatbot-glass-edge: color-mix(
                        in srgb,
                        var(--color-surface) 85%,
                        transparent
                    );

                    // Lighter than the surface: white on a light theme, a
                    // soft gray on a dark one
                    --chatbot-glass-light: color-mix(
                        in srgb,
                        color-mix(in oklab, var(--color-surface) 55%, white) 85%,
                        transparent
                    );

                    background-color: color-mix(
                        in srgb,
                        var(--color-surface) 72%,
                        transparent
                    );
                    backdrop-filter: blur(24px) saturate(180%);
                    box-shadow:
                        0 1px 2px rgb(0 0 0 / 6%),
                        0 16px 40px -12px rgb(0 0 0 / 24%),
                        0 40px 80px -32px rgb(0 0 0 / 28%);

                    // Above the sticky header, and every click goes through
                    &::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        z-index: 2;
                        border-radius: inherit;
                        padding: var(--spacing-px);
                        background: linear-gradient(
                            135deg,
                            var(--chatbot-glass-light),
                            var(--chatbot-glass-edge) 35%,
                            transparent 60%,
                            var(--chatbot-glass-edge)
                        );
                        mask:
                            linear-gradient(#000 0 0) content-box,
                            linear-gradient(#000 0 0);
                        mask-composite: exclude;
                        -webkit-mask-composite: xor;
                        pointer-events: none;
                    }

                    .pk-chatbot {
                        background-color: transparent;
                    }

                    .pk-chatbot__header {
                        background-color: color-mix(
                            in srgb,
                            var(--color-surface-1) 50%,
                            transparent
                        );
                        border-bottom-color: var(--chatbot-glass-edge);
                    }

                    .pk-chatbot-message:not(.pk-chatbot-message--user)
                        .pk-chatbot-message__text,
                    .pk-chatbot-message__loading-info,
                    .pk-chatbot-messages__scroll-to-bottom {
                        background-color: var(--chatbot-glass-pane);
                        border-color: var(--chatbot-glass-edge);
                        box-shadow: inset 0 1px 0 var(--chatbot-glass-light);
                    }

                    .pk-chatbot-message--user .pk-chatbot-message__text {
                        background-color: color-mix(
                            in srgb,
                            var(--chatbot-main-color, var(--color-surface-1))
                                78%,
                            transparent
                        );
                        border-color: color-mix(
                            in srgb,
                            var(--chatbot-main-color, var(--color-surface-3))
                                88%,
                            transparent
                        );
                        box-shadow: inset 0 1px 0 rgb(255 255 255 / 25%);
                    }

                    // The notice fills the field's top corners from behind
                    // it, which through two panes reads as a darker band:
                    // under a notice the field squares its top instead
                    .pk-chatbot-input__notice {
                        background-color: var(--chatbot-glass-pane);

                        &::after {
                            display: none;
                        }
                    }

                    .pk-chatbot-input:has(.pk-chatbot-input__notice)
                        .pk-chatbot-input__form {
                        border-top-left-radius: 0;
                        border-top-right-radius: 0;
                    }

                    .pk-chatbot-input__form {
                        background-color: var(--chatbot-glass-pane);

                        // The focus keeps its own border
                        &:not(:has(:focus-within)) {
                            border-color: var(--chatbot-glass-edge);
                        }
                    }

                    .pk-chatbot-input__textarea,
                    .pk-chatbot-input__counter {
                        background-color: transparent;
                    }

                    .pk-chatbot__footer {
                        border-top-color: var(--chatbot-glass-edge);
                    }

                    @media (prefers-reduced-transparency: reduce) {
                        background-color: var(--color-surface);
                        backdrop-filter: none;
                    }
                }
            }
        }

        // #endregion

        // Without the widget's button the card takes its place, at the
        // bottom corner
        &--no-button .floating-chat-widget__chat {
            @include media-breakpoint-up('sm', $breakpoints) {
                bottom: 16px;
            }
        }

        // #region copilot layout (spec widget-copilot.md)
        // Full screen below `md`, a drawer over a backdrop from `md`, docked
        // beside the framed site from `xl`: the widths of Agent Studio's own
        // copilot. These rules come after the floating ones and win on
        // specificity, so the floating layout stays as it was.
        &--copilot {
            // The panel and the beam on its border share one box, on the top
            // layer like the floating card; the frame and the backdrop are
            // one under it
            .floating-chat-widget__chat,
            .floating-chat-widget__panel-beam {
                position: fixed;
                inset: 0;
                width: auto;
                height: auto;
                border-radius: 0;
                box-shadow: none;
                z-index: 2147483647;

                @include media-breakpoint-up('md', $breakpoints) {
                    inset: var(--chatbot-copilot-top)
                        var(--chatbot-copilot-right)
                        var(--chatbot-copilot-bottom) auto;
                    width: var(--chatbot-copilot-panel-width);
                    border-radius: var(--rounded-xl);
                }
            }

            // The chat in its panel chrome, as Agent Studio docks it: a card
            // of its own while it overlays the site, then, docked, no card at
            // all, straight on the frame's surface beside the site's window
            .floating-chat-widget__chat {
                background-color: var(--color-surface);

                // Full screen: the header below the notch, the input above
                // the home indicator
                padding: env(safe-area-inset-top) env(safe-area-inset-right)
                    env(safe-area-inset-bottom) env(safe-area-inset-left);

                @include media-breakpoint-up('md', $breakpoints) {
                    padding: 0;
                    box-shadow: var(--shadow-xl);
                }

                @include media-breakpoint-up('xl', $breakpoints) {
                    background-color: transparent;
                    border-radius: 0;
                    box-shadow: none;
                    backdrop-filter: none;
                }
            }

            .floating-chat-widget__panel-beam {
                pointer-events: none;

                // Docked, the beam runs along the site's frame instead
                @include media-breakpoint-up('xl', $breakpoints) {
                    display: none;
                }
            }

            // The copilot hides the button while open: the close of the
            // sign-in and loading panels shows at every width
            .floating-chat-widget__close {
                display: block;
            }
        }

        &__backdrop {
            display: none;

            @include media-breakpoint-up('md', $breakpoints) {
                display: block;
                position: fixed;
                inset: 0;
                z-index: 2147483646;
                background-color: rgb(0 0 0 / 30%);
            }

            @include media-breakpoint-up('xl', $breakpoints) {
                display: none;
            }
        }

        // A window on the site: the frame's surface is the window's spread
        // shadow, so the site shows through the window only, and every
        // click passes to it.
        &__frame {
            display: none;

            @include media-breakpoint-up('xl', $breakpoints) {
                display: block;
                position: fixed;
                inset: 0;
                z-index: 2147483646;
                pointer-events: none;
            }
        }

        &__frame-window {
            position: absolute;
            inset: var(--chatbot-copilot-gap)
                calc(
                    var(--chatbot-copilot-panel-width) + 2 *
                        var(--chatbot-copilot-gap)
                )
                var(--chatbot-copilot-gap) var(--chatbot-copilot-gap);
            border: var(--spacing-px) solid var(--color-surface-3);
            border-radius: var(--rounded-xl);
            box-shadow: 0 0 0 100vmax var(--color-surface-2);
        }

        &--copilot.floating-chat-widget--position-left {
            .floating-chat-widget__chat,
            .floating-chat-widget__panel-beam {
                @include media-breakpoint-up('md', $breakpoints) {
                    inset: var(--chatbot-copilot-top) auto
                        var(--chatbot-copilot-bottom)
                        var(--chatbot-copilot-left);
                }
            }

            .floating-chat-widget__frame-window {
                inset: var(--chatbot-copilot-gap) var(--chatbot-copilot-gap)
                    var(--chatbot-copilot-gap)
                    calc(
                        var(--chatbot-copilot-panel-width) + 2 *
                            var(--chatbot-copilot-gap)
                    );
            }
        }

        // The sheet of the floating layout while the agent acts, below `md`
        // where this panel is full screen. The panel and its beam move in
        // step, and the slide that opens the panel keeps its transform
        &--copilot {
            .floating-chat-widget__chat,
            .floating-chat-widget__panel-beam {
                @media (width < 992px) {
                    transition:
                        top var(--chatbot-copilot-duration)
                            var(--chatbot-copilot-easing),
                        transform var(--chatbot-copilot-duration)
                            var(--chatbot-copilot-easing);
                }
            }
        }

        &--copilot.floating-chat-widget--acting {
            .floating-chat-widget__chat,
            .floating-chat-widget__panel-beam {
                @media (width < 992px) {
                    top: 62dvh;
                    border-radius: var(--rounded-xxl) var(--rounded-xxl) 0 0;
                }
            }

            // Away from the notch, the sheet needs no room for it
            .floating-chat-widget__chat {
                @media (width < 992px) {
                    padding-top: 0;
                }
            }
        }

        // #endregion
    }

    // #region copilot opening: in step with the frame, with
    // Agent Studio's duration and curve. The panel slides in from its side,
    // and the frame fades in while the window on the site closes in from
    // that side.
    .floating-chat-widget-frame-enter-active,
    .floating-chat-widget-frame-leave-active {
        transition: opacity var(--chatbot-copilot-duration)
            var(--chatbot-copilot-easing);

        .floating-chat-widget__frame-window {
            transition:
                left var(--chatbot-copilot-duration)
                    var(--chatbot-copilot-easing),
                right var(--chatbot-copilot-duration)
                    var(--chatbot-copilot-easing);
        }
    }

    .floating-chat-widget-frame-enter-from,
    .floating-chat-widget-frame-leave-to {
        opacity: 0;

        .floating-chat-widget__frame-window {
            right: var(--chatbot-copilot-gap);
        }
    }

    .floating-chat-widget-copilot-enter-active,
    .floating-chat-widget-copilot-leave-active {
        transition: transform var(--chatbot-copilot-duration)
            var(--chatbot-copilot-easing);
    }

    .floating-chat-widget-copilot-enter-from,
    .floating-chat-widget-copilot-leave-to {
        transform: translateX(calc(100% + var(--chatbot-copilot-right)));
    }

    .floating-chat-widget--position-left {
        .floating-chat-widget-frame-enter-from,
        .floating-chat-widget-frame-leave-to {
            .floating-chat-widget__frame-window {
                left: var(--chatbot-copilot-gap);
            }
        }

        .floating-chat-widget-copilot-enter-from,
        .floating-chat-widget-copilot-leave-to {
            transform: translateX(calc(-100% - var(--chatbot-copilot-left)));
        }
    }

    // #endregion
</style>
